import {
    useCallback,
} from 'react'
import {
    RequestKeys,
} from 'app/hooks/useRequest'
import useLabel from 'app/hooks/useLabel'
import {
    useGetRequestSpreadSheetAdapter,
} from 'app/shared-components/SkyNetSpreadSheet'
import useSimpleApiRequest from 'app/hooks/useSimpleApiRequest'
import StatusHandler from 'app/shared-components/StatusHandler'
import {
    AsyncMutationOptions,
} from 'app/types/request.types'
import useFilter from 'app/hooks/useFilter'
import {
    useSkyNetPagination, SkyNetPaginationContext,
} from 'app/shared-components/SkyNetPagination'
import requests from 'app/requests/RateCard/RateCard.requests'
import {
    StandardBaseLeaseFilterRequestResponse,
} from 'app/requests/RateCard/RateCard.types'
import BaseLeaseSpreadsheet from 'app/Apps/Rates/components/BaseLeaseSpreadsheet'
import defaultRateCardsSort from 'app/Apps/Rates/Rates.utils'

import {
    BaseLeaseEditable,
} from '../BaseLease.types'
import {
    prepareBaseLeaseToBulkEdit,
} from '../BaseLease.utils'

export default function BaseLeaseBulkEdit({
    onClose,
}: Readonly<{
    onClose: () => void
}>) {
    const l = useLabel()
    const filterOptions = useFilter<StandardBaseLeaseFilterRequestResponse[]>()
    const paginationOptions = useSkyNetPagination(100)

    const {
        isError,
        loaded,
        error,
        refetch,
    } = useGetRequestSpreadSheetAdapter<StandardBaseLeaseFilterRequestResponse>({
        config: requests.baseLeaseFilterRequest,
        requestKey: RequestKeys.getBaseLeaseRateCards,
        enabled: true,
        filterOptions,
        paginationOptions,
        defaultSort: defaultRateCardsSort,
    })

    const {
        mutateAsync: bulkEdit,
    } = useSimpleApiRequest<{ items: BaseLeaseEditable[] }>({
        name: 'BaseLeaseBulkUpdate',
        title: l('BASE_LEASE_RATE_CARDS_UPDATED'),
        requestParams: requests.baseLeaseBulkEdit(),
        onSuccess: refetch,
    })

    const onSave = useCallback(async (
        items: StandardBaseLeaseFilterRequestResponse[],
        options?: AsyncMutationOptions,
    ) => {
        await bulkEdit({
            items: prepareBaseLeaseToBulkEdit(items),
        }, {
            ...options,
            onSuccess: options?.onSuccess || onClose,
        })
    }, [
        bulkEdit,
        onClose,
    ])

    return (
        <StatusHandler
            isError={isError}
            isFetching={!loaded}
            error={error}
            isSuccess={loaded}
        >
            <SkyNetPaginationContext.Provider value={paginationOptions}>
                <BaseLeaseSpreadsheet
                    filterOptions={filterOptions}
                    onCancel={onClose}
                    onSave={onSave}
                    filterConfigRequest={requests.baseLeaseFilterCount}
                />
            </SkyNetPaginationContext.Provider>
        </StatusHandler>
    )
}
